import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@@/ui/primitives';
import { z } from 'zod/v3';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BannerConfigurationForm } from './BannerConfigurationForm';
import { useBannerManagement } from './hooks/useBannerManagement';

import { ConfirmationModal, Spinner } from '@@/ui/components';
import BannerSaveConfirmationModal from './BannerSaveConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { BannerSeverityMap } from '@@/api/api-outage-notifications';
import type { BannerFormValues } from './types/banner';

const formSchema = z.object({
  bannerVisibility: z.boolean(),
  severity: z.enum(['low', 'moderate', 'high', 'critical']),
  bannerText: z.string().min(1, { message: 'Banner text is required' }),
  selectedLocations: z.array(z.string()),
  locationInput: z.string(),
  hideLogin: z.boolean()
});

export const BannerConfiguration: React.FC = () => {
  const { formData, isLoading, saveBanner, isSaving } = useBannerManagement();
  const initializedRef = useRef(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  type SaveFormData = Omit<BannerFormValues, 'locationInput'>;
  const pendingValuesRef = useRef<SaveFormData | null>(null);
  const navigate = useNavigate();

  const form = useForm<BannerFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bannerVisibility: false,
      severity: BannerSeverityMap.Low,
      bannerText: '',
      selectedLocations: [],
      locationInput: '',
      hideLogin: false
    }
  });

  const { reset, getValues, formState } = form;

  useEffect(() => {
    if (formData && !initializedRef.current) {
      reset({
        bannerVisibility: formData.bannerVisibility,
        severity: formData.severity as BannerFormValues['severity'],
        bannerText: formData.bannerText,
        selectedLocations: formData.selectedLocations,
        locationInput: '',
        hideLogin: formData.hideLogin
      });
      initializedRef.current = true;
    }
  }, [formData, reset]);

  // Warn on browser/tab close or refresh when there are unsaved changes
  useEffect(() => {
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (formState.isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
  }, [formState.isDirty]);

  const handleSave = () => {
    const values = getValues();
    const { locationInput: _ignored, ...toSave } = values;

    // If we don't yet have baseline data, proceed to save immediately
    if (!initializedRef.current || !formData) {
      saveBanner(toSave);
      return;
    }

    const hideLoginChanged = values.hideLogin !== formData.hideLogin;
    const visibilityChanged = values.bannerVisibility !== formData.bannerVisibility;

    if (hideLoginChanged || visibilityChanged) {
      pendingValuesRef.current = toSave;
      setShowSaveConfirm(true);
      return;
    }

    // No impactful changes, save immediately
    saveBanner(toSave);
  };

  const confirmSave = () => {
    if (!pendingValuesRef.current) {
      setShowSaveConfirm(false);
      return;
    }
    const v = pendingValuesRef.current;
    saveBanner(v);
    setShowSaveConfirm(false);
    pendingValuesRef.current = null;
  };

  const handleCancel = () => {
    if (formState.isDirty) {
      setShowLeaveConfirm(true);
      return;
    }
    navigate(-1);
  };

  const confirmLeave = () => {
    setShowLeaveConfirm(false);
    navigate(-1);
  };

  return (
    <div className='col-span-15 flex flex-col gap-1'>
      {/* Header Section */}
      <div className='bg-white px-12 py-6 border border-[#E5E7EB] rounded-tr-[10px]'>
        <div className='flex justify-between items-center'>
          <h2 className='text-lg font-semibold'>Banner Configuration</h2>
          <div className='flex gap-2'>
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className='px-4 py-2 text-[#8F96A6] font-medium bg-transparent border-none cursor-pointer hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Cancel
            </button>
            <Button
              variant='default'
              onClick={handleSave}
              disabled={isSaving}
              className='bg-cxcentral-green hover:bg-[#0d9196] text-white disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      {/* Body Section */}
      <div className='bg-white pt-6 px-12 pb-0 border border-[#E5E7EB] rounded-br-[10px] flex flex-col flex-1'>
        {isLoading ? (
          <div className='flex items-center justify-center py-16'>
            <Spinner />
          </div>
        ) : (
          <BannerConfigurationForm form={form} />
        )}

        {/* Horizontal Line at Bottom */}
        <div className='mt-auto border-t border-[#E5E7EB] mt-12 pb-6' />
      </div>

      <ConfirmationModal
        title='Discard changes?'
        description='You have unsaved changes. Are you sure you want to leave this page?'
        confirmButtonText='Leave without saving'
        cancelButtonText='Stay on page'
        destructive
        open={showLeaveConfirm}
        onOpenChange={setShowLeaveConfirm}
        onConfirm={confirmLeave}
      />
      <BannerSaveConfirmationModal
        open={showSaveConfirm}
        onOpenChange={setShowSaveConfirm}
        onConfirm={confirmSave}
        isLoading={isSaving}
        originalHideLogin={!!formData?.hideLogin}
        newHideLogin={pendingValuesRef.current?.hideLogin ?? formData?.hideLogin ?? false}
        originalVisibility={!!formData?.bannerVisibility}
        newVisibility={pendingValuesRef.current?.bannerVisibility ?? formData?.bannerVisibility ?? false}
      />
    </div>
  );
};
